<template>
    <v-container fluid>
        <v-card class="mb-5 pa-2">
            <v-form v-if="reseller" @submit.prevent="save">
                <card-header :title="$t('reseler-edit.texts.title')">
                </card-header>
                <v-card-text>
                    <v-row>
                        <v-col cols="12" xs="12" sm="12" md="5">
                            <v-text-field
                                flat
                                v-model="reseller.trade_name"
                                :label="$t('reseler-edit.labels.trade-name')"
                                :counter="120"
                                :error-messages="tradeNameErrors"
                                @input="$v.reseller.trade_name.$touch()"
                                @blur="$v.reseller.trade_name.$touch()"
                                autofocus
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" xs="12" sm="12" md="5">
                            <v-text-field
                                flat
                                v-model="reseller.legal_name"
                                :label="$t('reseler-edit.labels.legal-name')"
                                :counter="180"
                                :error-messages="legalNameErrors"
                                @input="$v.reseller.legal_name.$touch()"
                                @blur="$v.reseller.legal_name.$touch()"
                            ></v-text-field>
                        </v-col>
                        <v-col cols="12" xs="12" sm="12" md="2">
                            <v-text-field
                                flat
                                v-model="reseller.tenant_code"
                                :label="$t('reseler-edit.labels.tenant-code')"
                                :error-messages="tenantCodeErrors"
                                @input="$v.reseller.tenant_code.$touch()"
                                @blur="$v.reseller.tenant_code.$touch()"
                            ></v-text-field>
                        </v-col>
                    </v-row>
                    <v-row>
                        <v-col
                            justifyContent:flex-end
                            cols="12"
                            xs="6"
                            sm="6"
                            md="6"
                            class="grey lighten-4"
                        >
                            <v-card>
                                <v-card-title class="subtitle-2"
                                    >Adicionar Logo</v-card-title
                                >
                                <v-card-text>
                                    <v-card v-if="previewLogo">
                                        <v-img
                                            :src="previewLogo"
                                            max-height="80"
                                            contain
                                        ></v-img>
                                    </v-card>
                                    <v-card
                                        v-else
                                        color="grey lighten-2"
                                        class="text-center pa-3 mb-5"
                                    >
                                        <v-icon color="grey">
                                            mdi-image-search
                                        </v-icon>
                                    </v-card>
                                </v-card-text>
                                <v-card-actions>
                                    <v-file-input
                                        :label="
                                            $t(
                                                'reseler-edit.labels.logo-pick',
                                            ) + '...'
                                        "
                                        v-model="reseller.logo"
                                        @change="handlePreviewLogo"
                                    ></v-file-input>
                                </v-card-actions>
                            </v-card>
                        </v-col>

                        <v-col
                            cols="12"
                            xs="6"
                            sm="6"
                            md="6"
                            class="grey lighten-4"
                        >
                            <v-card>
                                <v-card-title class="subtitle-2"
                                    >Adicionar Ícone</v-card-title
                                >
                                <v-card-text>
                                    <v-img
                                        v-if="previewFavicon"
                                        :src="previewFavicon"
                                        aspect-ratio="1"
                                        class="d-flex align-start"
                                        max-height="80"
                                        max-width="80"
                                        contain
                                    />
                                    <v-avatar
                                        v-else
                                        color="grey lighten-2"
                                        class="text-center pa-5 mb-5"
                                    >
                                        <v-icon color="grey"
                                            >mdi-image-outline</v-icon
                                        >
                                    </v-avatar>
                                </v-card-text>
                                <v-card-actions>
                                    <v-file-input
                                        :label="
                                            $t(
                                                'reseler-edit.labels.favicon-pick',
                                            ) + '...'
                                        "
                                        v-model="reseller.favicon"
                                        @change="handlePreviewFavicon"
                                    ></v-file-input>
                                </v-card-actions>
                            </v-card>
                        </v-col>
                    </v-row>
                </v-card-text>

                <form-actions :loadingSave="loading"></form-actions>
            </v-form>
            <template v-else>
                <v-row>
                    <v-col class="text-center">
                        <v-progress-circular
                            :size="50"
                            color="primary"
                            indeterminate
                        ></v-progress-circular>
                    </v-col>
                </v-row>
            </template>
        </v-card>
        <confirm
            ref="confirm"
            :persistent="true"
            width="500px"
            confirmColor="green"
            confirmText="Alterar"
        ></confirm>
    </v-container>
</template>

<script>
import { validationMixin } from 'vuelidate'
import { required, maxLength } from 'vuelidate/lib/validators'
import Confirm from '@/components/dialogs/Confirm'
import CardHeader from '@/components/utils/CardHeader'
import FormActions from '@/components/utils/FormActions'
import { mapGetters } from 'vuex'

export default {
    components: {
        confirm: Confirm,
        'card-header': CardHeader,
        'form-actions': FormActions,
    },

    data: () => {
        return {
            reseller: null,
            loading: false,
            oldTenantCode: null,
            previewLogo: '',
            previewFavicon: '',
        }
    },

    mixins: [validationMixin],

    validations: {
        reseller: {
            trade_name: { required, maxLength: maxLength(120) },
            legal_name: { maxLength: maxLength(180) },
            tenant_code: { required, maxLength: maxLength(20) },
        },
    },

    methods: {
        async save() {
            if (this.reseller.tenant_code != this.oldTenantCode) {
                let confirm = await this.$refs.confirm.open(
                    this.$t('reseler-edit.messages.tenant-code-alteration'),
                    this.$t(
                        'reseler-edit.messages.tenant-code-alteration-confirmation',
                    ),
                )

                if (!confirm) return
            }

            this.loading = true

            this.$v.$touch()

            if (this.$v.$invalid) {
                this.$bus.$emit(
                    'notify',
                    'error',
                    this.$t('reseler-edit.bus.save-error'),
                )

                this.loading = false
                return
            }

            let res = null
            let reseller = this.$lodash.cloneDeep(this.reseller)

            if (reseller.logo) {
                reseller.logo = await this.toBase64(reseller.logo)
            }

            if (reseller.favicon) {
                reseller.favicon = await this.toBase64(reseller.favicon)
            }

            try {
                res = await this.$http.post('reseller', reseller).catch(() => {
                    this.$bus.$emit(
                        'notify',
                        'error',
                        this.$t('reseler-edit.bus.request-error'),
                    )
                })
            } finally {
                this.loading = false
            }

            if (!res) return

            this.$store.dispatch('reseller/setReseller', res.data)
            this.$store.dispatch('auth/setTenantCode', res.data.tenant_code)

            this.$bus.$emit(
                'notify',
                'success',
                this.$t('reseler-edit.bus.save-success', {
                    'trade-name': res.data.trade_name,
                }),
            )

            this.loading = false
        },

        toBase64(file) {
            return new Promise((resolve, reject) => {
                const reader = new FileReader()
                reader.readAsDataURL(file)
                reader.onload = () => resolve(reader.result)
                reader.onerror = error => reject(error)
            })
        },

        async handlePreviewLogo() {
            if (this.reseller.logo) {
                let logo = await this.toBase64(this.reseller.logo)

                if (logo) {
                    this.previewLogo = logo
                }

                return
            }

            this.previewLogo = ''
        },

        async handlePreviewFavicon() {
            if (this.reseller.favicon) {
                let favicon = await this.toBase64(this.reseller.favicon)

                if (favicon) {
                    this.previewFavicon = favicon
                }

                return
            }

            this.previewFavicon = ''
        },
    },

    created() {
        let reseller = this.$lodash.cloneDeep(
            this.$store.getters['reseller/reseller'],
        )

        this.oldTenantCode = reseller.tenant_code

        this.previewLogo = reseller.brand.logo ? reseller.brand.logo_url : null
        this.previewFavicon = reseller.brand.favicon
            ? reseller.brand.favicon_url
            : null

        this.reseller = reseller
    },

    computed: {
        ...mapGetters({
            isWhitelabel: 'reseller/isWhitelabel',
        }),

        tradeNameErrors() {
            const errors = []
            if (!this.$v.reseller.trade_name.$dirty) return errors

            !this.$v.reseller.trade_name.maxLength &&
                errors.push(
                    this.$t(
                        'reseler-edit.messages.trade-name-error-max-length',
                    ),
                )

            !this.$v.reseller.trade_name.required &&
                errors.push(
                    this.$t('reseler-edit.messages.trade-name-error-required'),
                )
            return errors
        },

        legalNameErrors() {
            const errors = []
            if (!this.$v.reseller.legal_name.$dirty) return errors

            !this.$v.reseller.legal_name.maxLength &&
                errors.push(
                    this.$t(
                        'reseler-edit.messages.legal-name-error-max-length',
                    ),
                )

            return errors
        },

        tenantCodeErrors() {
            const errors = []
            if (!this.$v.reseller.tenant_code.$dirty) return errors

            !this.$v.reseller.tenant_code.maxLength &&
                errors.push(
                    this.$t(
                        'reseler-edit.messages.tenant-code-error-max-length',
                    ),
                )

            !this.$v.reseller.tenant_code.required &&
                errors.push(
                    this.$t('reseler-edit.messages.tenant-code-error-required'),
                )
            return errors
        },
    },
}
</script>

<style></style>
